import { ApiError, apiRequest } from "./client";
import type { AiChatRequest, AiChatResponse } from "../types";

export interface AiConnection {
  id: string;
  name: string;
  token_prefix: string;
  provider: "claude" | "codex" | null;
  online: boolean;
}

interface Job {
  id: string;
  state: "queued" | "running" | "completed" | "cancelled" | "failed";
  result?: AiChatResponse;
  error?: string;
}

const base = "/api/v1/me/ai-connections";

export const listAiConnections = () => apiRequest<AiConnection[]>(base);

function pause(signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const cancelled = () => {
      window.clearTimeout(timer);
      signal.removeEventListener("abort", cancelled);
      reject(new DOMException("Cancelled", "AbortError"));
    };
    const timer = window.setTimeout(() => {
      signal.removeEventListener("abort", cancelled);
      resolve();
    }, 1000);
    signal.addEventListener("abort", cancelled, { once: true });
    if (signal.aborted) cancelled();
  });
}

export async function chatWithConnection(tokenId: string, reportId: string, request: AiChatRequest, signal: AbortSignal): Promise<AiChatResponse> {
  // Keep the initial short POST alive so cancellation can always delete a job it creates.
  let job = await apiRequest<Job>(`${base}/jobs`, { method: "POST", body: { token_id: tokenId, report_id: reportId, request } });
  const cancel = () => { void apiRequest<void>(`${base}/jobs/${job.id}`, { method: "DELETE" }).catch(() => undefined); };
  signal.addEventListener("abort", cancel, { once: true });
  try {
    if (signal.aborted) {
      cancel();
      throw new DOMException("Cancelled", "AbortError");
    }
    const deadline = Date.now() + 330_000;
    while (job.state === "queued" || job.state === "running") {
      if (Date.now() > deadline) {
        cancel();
        throw new ApiError(504, "The selected AI took too long to answer. Your dashboard was not changed.");
      }
      await pause(signal);
      job = await apiRequest<Job>(`${base}/jobs/${job.id}`, { signal });
    }
    if (job.state === "completed" && job.result) return job.result;
    throw new ApiError(502, job.error || "The AI request was cancelled. Your dashboard was not changed.");
  } catch (error) {
    cancel();
    throw error;
  } finally {
    signal.removeEventListener("abort", cancel);
  }
}
